import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

// assets
import SearchOutlined from '@ant-design/icons/SearchOutlined';

import { searchGlobal } from 'services/searchApi';

// ==============================|| HEADER CONTENT - SEARCH ||============================== //

export default function Search() {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const searchText = query.trim();
    if (!searchText) {
      setResults([]);
      return;
    }

    let active = true;
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await searchGlobal(searchText, 12);
        if (active) {
          setResults(data);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [query]);

  const handleSelect = (route) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(route);
  };

  return (
    <Box sx={{ width: '100%', ml: { xs: 0, md: 1 }, position: 'relative' }}>
      <FormControl sx={{ width: { xs: '100%', md: 300 } }}>
        <OutlinedInput
          size="small"
          id="header-search"
          inputRef={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && results.length > 0) {
              handleSelect(results[0].route);
            }
          }}
          startAdornment={
            <InputAdornment position="start" sx={{ mr: -0.5 }}>
              <SearchOutlined />
            </InputAdornment>
          }
          endAdornment={loading ? <CircularProgress size={16} /> : null}
          aria-describedby="header-search-text"
          placeholder="Ara: müşteri, iş emri, ürün, istasyon..."
        />
      </FormControl>

      {open && (query.trim() || results.length > 0) && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 360,
            overflow: 'auto'
          }}
        >
          {results.length === 0 ? (
            <Box sx={{ p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Sonuç bulunamadı.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {results.map((item) => (
                <ListItemButton key={`${item.entityType}-${item.id}`} onMouseDown={() => handleSelect(item.route)}>
                  <ListItemText
                    primary={`${item.code} · ${item.title}`}
                    secondary={`${item.entityType}${item.subtitle ? ` · ${item.subtitle}` : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
}
